import React, { useState } from 'react';
import { Search, Loader2, AlertCircle, FileText, Calendar, Building2, TrendingUp, ShieldAlert, DollarSign, Activity, Target } from 'lucide-react';

const CrawlerModule = ({ apiBaseUrl }) => {
  const [numero, setNumero] = useState('');
  const [loading, setLoading] = useState(false);
  const [resultado, setResultado] = useState(null);
  const [erro, setErro] = useState('');

  const formatarProcesso = (value) => {
    // Apenas números
    const v = value.replace(/\D/g, '');
    // Máscara: 0000000-00.0000.0.00.0000
    let formatado = v;
    if (v.length > 7) formatado = v.replace(/^(\d{7})(\d)/, '$1-$2');
    if (v.length > 9) formatado = formatado.replace(/^(\d{7})-(\d{2})(\d)/, '$1-$2.$3');
    if (v.length > 13) formatado = formatado.replace(/^(\d{7})-(\d{2})\.(\d{4})(\d)/, '$1-$2.$3.$4');
    if (v.length > 14) formatado = formatado.replace(/^(\d{7})-(\d{2})\.(\d{4})\.(\d{1})(\d)/, '$1-$2.$3.$4.$5');
    if (v.length > 16) formatado = formatado.replace(/^(\d{7})-(\d{2})\.(\d{4})\.(\d{1})\.(\d{2})(\d)/, '$1-$2.$3.$4.$5.$6');
    
    return formatado.slice(0, 25);
  };

  const handleConsultar = async (e) => {
    e.preventDefault();
    if (!numero || numero.length < 25) {
      setErro('Digite um número de processo válido (20 dígitos).');
      return;
    }

    setLoading(true);
    setErro('');
    setResultado(null);

    try {
      // Nota: No código do backend, o número estava fixo como "0000000-00.0000.0.00.0000" para testes.
      // O backend deve ser ajustado para pegar do req.body se quiser a consulta real pelo número digitado.
      // Aqui mandaremos via body de qualquer forma.
      const response = await fetch(`${apiBaseUrl}/api/crawler`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ numero }),
      });

      if (!response.ok) {
        throw new Error('Falha ao consultar o processo no STJ.');
      }

      const data = await response.json();
      
      if (data.success) {
        setResultado(data.dados);
      } else {
        throw new Error(data.error || 'Erro desconhecido');
      }
    } catch (err) {
      setErro(err.message || 'Erro ao conectar com o servidor.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6 animate-in fade-in duration-500">
      <div>
        <h2 className="text-2xl font-black text-slate-800 tracking-tight">Crawler Jurídico (STJ)</h2>
        <p className="text-slate-500 text-sm font-medium">Consulte dados de processos diretamente na base do Superior Tribunal de Justiça.</p>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="p-8 border-b border-slate-100 bg-slate-50">
          <form onSubmit={handleConsultar} className="max-w-2xl mx-auto flex gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={20} />
              <input
                type="text"
                placeholder="0000000-00.0000.0.00.0000"
                value={numero}
                onChange={(e) => setNumero(formatarProcesso(e.target.value))}
                className="w-full pl-12 pr-4 py-4 bg-white border border-slate-300 rounded-xl text-lg font-medium outline-none focus:ring-4 focus:ring-blue-500/20 focus:border-blue-500 transition-all shadow-sm"
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 rounded-xl font-bold shadow-lg shadow-blue-200 transition-all active:scale-95 flex items-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
            >
              {loading ? (
                <Loader2 className="animate-spin" size={20} />
              ) : (
                <Search size={20} />
              )}
              {loading ? 'Buscando...' : 'Consultar'}
            </button>
          </form>
          {erro && (
            <div className="max-w-2xl mx-auto mt-4 p-4 bg-red-50 text-red-600 rounded-xl flex items-center gap-2 font-medium text-sm border border-red-100">
              <AlertCircle size={18} />
              {erro}
            </div>
          )}
        </div>

        {/* Resultado */}
        <div className="p-8 min-h-[400px] bg-slate-50 flex flex-col justify-center">
          {loading ? (
            <div className="flex flex-col items-center justify-center text-slate-400 gap-4">
              <Loader2 className="animate-spin text-blue-600" size={48} />
              <p className="font-medium animate-pulse">Consultando e gerando análise inteligente...</p>
            </div>
          ) : resultado ? (
            <div className="space-y-6 max-w-4xl mx-auto w-full">
              {/* Header do Processo */}
              <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-6 flex items-center gap-4">
                <div className="w-16 h-16 bg-blue-50 rounded-2xl flex items-center justify-center text-blue-600 shrink-0">
                  <Building2 size={32} />
                </div>
                <div className="flex-1">
                  <h3 className="font-black text-2xl text-slate-800 tracking-tight">{resultado.numero || resultado.processo}</h3>
                  <div className="flex gap-2 mt-2">
                    <span className="px-3 py-1 bg-slate-100 text-slate-600 rounded-md text-xs font-bold uppercase tracking-wider">
                      {resultado.tribunal}
                    </span>
                    {resultado.status && (
                      <span className="px-3 py-1 bg-blue-50 text-blue-600 rounded-md text-xs font-bold uppercase tracking-wider">
                        {resultado.status}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Analise de Risco */}
                <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-6 flex flex-col">
                  <h4 className="text-sm font-bold uppercase tracking-wider text-slate-400 flex items-center gap-2 mb-4">
                    <Activity size={16} /> Status e Análise
                  </h4>
                  <div className="space-y-4 flex-1 flex flex-col">
                    <div>
                      <p className="text-xs text-slate-500 font-semibold mb-1">Fase Atual</p>
                      <p className="text-slate-800 font-medium">{resultado.fase || "Consulta STJ"}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 font-semibold mb-1">Última Movimentação</p>
                      <p className="text-slate-800 font-medium bg-slate-50 p-3 rounded-lg text-sm border border-slate-100">
                        {resultado.ultima_movimentacao || resultado.titulo || "Informação não disponível"}
                      </p>
                    </div>
                    {resultado.risco && (
                      <div className="flex gap-4 pt-2 mt-auto">
                        <div className="flex-1 bg-slate-50 p-3 rounded-xl border border-slate-100">
                          <p className="text-xs text-slate-500 font-semibold mb-1">Risco Estimado</p>
                          <p className={`font-bold ${resultado.risco === 'Alto' ? 'text-red-600' : resultado.risco === 'Médio' ? 'text-orange-500' : 'text-green-600'}`}>
                            {resultado.risco}
                          </p>
                        </div>
                        <div className="flex-1 bg-slate-50 p-3 rounded-xl border border-slate-100">
                          <p className="text-xs text-slate-500 font-semibold mb-1">Prob. de Êxito</p>
                          <p className="font-bold text-blue-600">{resultado.probabilidade_exito}</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Projeção Financeira */}
                {resultado.projecao_financeira ? (
                  <div className="bg-gradient-to-br from-slate-800 to-slate-900 border border-slate-700 rounded-2xl shadow-lg p-6 text-white relative overflow-hidden flex flex-col">
                    <DollarSign className="absolute -right-4 -bottom-4 text-white/5" size={120} />
                    <h4 className="text-sm font-bold uppercase tracking-wider text-slate-300 flex items-center gap-2 mb-4 relative z-10">
                      <TrendingUp size={16} /> Projeção Financeira
                    </h4>
                    
                    <div className="space-y-4 relative z-10 flex-1 flex flex-col">
                      <div className="bg-white/10 p-4 rounded-xl border border-white/10 backdrop-blur-sm">
                        <p className="text-xs text-slate-300 font-semibold mb-1">Valor Estimado da Causa</p>
                        <p className="text-2xl font-black text-white">
                          {resultado.projecao_financeira.valor_estimado.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                        </p>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-3 mt-auto">
                        <div className="bg-white/5 p-3 rounded-xl border border-white/5">
                          <p className="text-xs text-slate-400 font-semibold mb-1">Honorários Previstos</p>
                          <p className="font-bold text-blue-300">
                            {resultado.projecao_financeira.honorarios.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                          </p>
                        </div>
                        <div className="bg-white/5 p-3 rounded-xl border border-white/5">
                          <p className="text-xs text-slate-400 font-semibold mb-1">Custos Estimados</p>
                          <p className="font-bold text-red-300">
                            {resultado.projecao_financeira.custo_processo.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                          </p>
                        </div>
                      </div>

                      <div className="pt-3 border-t border-white/10 mt-3">
                        <div className="flex justify-between items-center">
                          <p className="text-sm text-slate-300 font-bold">Lucro Previsto</p>
                          <p className="text-xl font-black text-emerald-400">
                            {resultado.projecao_financeira.lucro_previsto.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="bg-slate-50 border border-slate-200 rounded-2xl p-6 flex flex-col items-center justify-center text-slate-400 text-center">
                    <DollarSign size={32} className="mb-2 opacity-50" />
                    <p className="text-sm font-medium">Projeção financeira não disponível para este processo.</p>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center text-slate-300 gap-4">
              <Search size={64} className="opacity-20" />
              <p className="font-medium">Digite um número de processo acima para realizar a consulta.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CrawlerModule;
